import { FocusImplementer, NumberEditor } from '@approvalmax/ui';
import { Button } from '@approvalmax/ui/src/components';
import { intl, numberHelpers } from '@approvalmax/utils';
import { constants } from 'modules/common';
import AttachmentsDropdown from 'modules/components/Attachment/AttachmentsDropdown';
import moment from 'moment';
import { ReadOnlyCell } from 'pages/requestForm/components';
import { NetSuiteApplyListLineExpanded } from 'shared/states';

import { ColumnDefinition } from '../../../../selectors/types/ColumnDefinition';
import { isFieldVisible } from '../../NetSuiteRequestForm/NetSuiteRequestForm.helpers';
import { messages } from './NetSuiteApplyList.messages';
import { ApplyListColumnOptions, ExtendedColumnDefinition } from './NetSuiteApplyList.types';

const { netSuiteConstants } = constants;

export const getColumnDefinitions = ({
    showErrors,
    fieldSettings,
    currencyPrecision,
    activeAttachmentId,
    requestIdsWithStatusError,
    requestIdsWithPaymentError,
    onSelectBill,
    onSelectAttachment,
    onApplyListDiscountAmountChange,
    onApplyListPaymentAmountChange,
}: ApplyListColumnOptions): ColumnDefinition<NetSuiteApplyListLineExpanded>[] => {
    const columnDef: ExtendedColumnDefinition[] = [
        {
            id: 'date-due',
            name: messages.dateDueColumnName,
            readonly: () => true,
            cell: ({ dateDue, requestId }) =>
                dateDue && requestId ? (
                    <Button link size='small' onClick={() => onSelectBill(requestId)} variant='text' color='blue100'>
                        {moment.utc(dateDue).format('ll')}
                    </Button>
                ) : (
                    <ReadOnlyCell text={dateDue ? moment.utc(dateDue).format('ll') : ''} />
                ),
            isHidden: !isFieldVisible(fieldSettings.dateDue),
        },
        {
            id: 'type',
            name: messages.typeColumnName,
            readonly: () => true,
            cell: ({ type, requestId }) =>
                type && requestId ? (
                    <Button link size='small' onClick={() => onSelectBill(requestId)} variant='text' color='blue100'>
                        {type}
                    </Button>
                ) : (
                    <ReadOnlyCell text={type ?? ''} />
                ),
            isHidden: !isFieldVisible(fieldSettings.type),
        },
        {
            id: 'reference',
            name: messages.referenceColumnName,
            readonly: () => true,
            cell: ({ reference }) => <ReadOnlyCell text={reference ?? ''} />,
            isHidden: !isFieldVisible(fieldSettings.reference),
        },
        {
            id: 'original-amount',
            name: messages.originalAmountColumnName,
            alignRight: true,
            readonly: () => true,
            cell: ({ originalAmount }) => <ReadOnlyCell text={intl.formatNumber(originalAmount, currencyPrecision)} />,
            isHidden: !isFieldVisible(fieldSettings.originalAmount),
        },
        {
            id: 'amount-due',
            name: messages.amountDueColumnName,
            alignRight: true,
            readonly: () => true,
            cell: ({ amountDue }) => <ReadOnlyCell text={intl.formatNumber(amountDue, currencyPrecision)} />,
            isHidden: !isFieldVisible(fieldSettings.amountDue),
        },
        {
            id: 'currency',
            name: messages.currencyColumnName,
            readonly: () => true,
            cell: ({ currencyName }) => <ReadOnlyCell text={currencyName ?? ''} />,
            isHidden: !isFieldVisible(fieldSettings.currency),
        },
        {
            id: 'discount-date',
            name: messages.discountDateColumnName,
            readonly: () => true,
            cell: ({ discountDate }) => (
                <ReadOnlyCell text={discountDate ? moment.utc(discountDate).format('ll') : ''} />
            ),
            isHidden: !isFieldVisible(fieldSettings.discountDate),
        },
        {
            id: 'discount-available',
            name: messages.discountAvailableColumnName,
            alignRight: true,
            readonly: () => true,
            cell: ({ discountAvailable }) => (
                <ReadOnlyCell text={intl.formatNumber(discountAvailable, currencyPrecision)} />
            ),
            isHidden: !isFieldVisible(fieldSettings.discountAvailable),
        },
        {
            id: 'discount-amount',
            name: messages.discountAmountColumnName,
            alignRight: true,
            readonly: ({ requestId }) => (requestId ? requestIdsWithStatusError.includes(requestId) : false),
            cell: ({ id, discountAmount }, { readonly }) =>
                readonly ? (
                    <ReadOnlyCell
                        text={discountAmount !== null ? intl.formatNumber(discountAmount, currencyPrecision) : ''}
                    />
                ) : (
                    <FocusImplementer>
                        {({ registerFocusable }) => (
                            <NumberEditor
                                ref={registerFocusable}
                                allowFloat
                                value={discountAmount !== null ? discountAmount : null}
                                onChange={(newValue: number | null) => {
                                    onApplyListDiscountAmountChange(id, newValue);
                                }}
                                placeholder={messages.discountAmountFieldPlaceholder}
                                theme='transparent'
                                align='right'
                                max={netSuiteConstants.unitPriceMaxValue}
                                min={-netSuiteConstants.unitPriceMaxValue}
                                precision={currencyPrecision && 8}
                                displayFormat='currency'
                                changeOnBlur={false}
                            />
                        )}
                    </FocusImplementer>
                ),
            isHidden: !isFieldVisible(fieldSettings.discountAmount),
        },
        {
            id: 'payment-amount',
            name: messages.paymentAmountColumnName,
            alignRight: true,
            invalid: ({ isEmpty, paymentAmount, requestId }) =>
                showErrors &&
                (isEmpty ||
                    !numberHelpers.isNumber(paymentAmount) ||
                    Boolean(requestId && requestIdsWithPaymentError.includes(requestId))),
            readonly: ({ requestId }) => (requestId ? requestIdsWithStatusError.includes(requestId) : false),
            cell: ({ id, paymentAmount }, { invalid, readonly }) =>
                readonly ? (
                    <ReadOnlyCell
                        text={paymentAmount !== null ? intl.formatNumber(paymentAmount, currencyPrecision) : ''}
                    />
                ) : (
                    <FocusImplementer>
                        {({ registerFocusable }) => (
                            <NumberEditor
                                ref={registerFocusable}
                                allowFloat
                                invalid={invalid}
                                value={paymentAmount !== null ? paymentAmount : null}
                                onChange={(newValue: number | null) => {
                                    onApplyListPaymentAmountChange(id, newValue);
                                }}
                                placeholder={messages.paymentAmountFieldPlaceholder}
                                theme='transparent'
                                align='right'
                                max={netSuiteConstants.unitPriceMaxValue}
                                min={-netSuiteConstants.unitPriceMaxValue}
                                precision={currencyPrecision && 8}
                                displayFormat='currency'
                                changeOnBlur={false}
                            />
                        )}
                    </FocusImplementer>
                ),
            isHidden: !isFieldVisible(fieldSettings.paymentAmount),
        },
        {
            id: 'attachments',
            name: '',
            readonly: ({ requestId }) => (requestId ? requestIdsWithStatusError.includes(requestId) : false),
            cell: ({ attachments, requestId, friendlyName }) =>
                attachments && attachments.length > 0 ? (
                    <AttachmentsDropdown
                        attachments={attachments}
                        activeAttachmentId={activeAttachmentId}
                        onSelectAttachment={(attachmentId: string) =>
                            requestId && friendlyName && onSelectAttachment(attachmentId, requestId, friendlyName)
                        }
                    />
                ) : null,
            fixedWidth: 60,
        },
    ];

    return columnDef.filter(({ isHidden }) => !isHidden);
};
